/**
 * RiddimTiles — «Riddim» as tiles in two columns. Task 69.
 *
 * `docs/superpowers/specs/2026-10-25-oblik-afisha-design.md` «Экраны → «Треки»»: a coloured bar
 * along the top — gold on every third tile, green on the others — the word «Riddim», the name
 * large, and how many tracks it opens. The order is the server's, biggest first, as before: the
 * bill changed how a riddim looks, not which comes first.
 *
 * A row of the virtual list is a pair of tiles and the gap under it, {@link RIDDIM_ROW_HEIGHT},
 * equal to `--riddim-h` plus `--s-3` — declared, like every row of this app.
 */

import type { JSX } from 'react';
import { useCallback, useMemo } from 'react';

import type { RiddimItem } from '../lib/api';
import { trackCount } from '../lib/format';
import type { Collection } from '../store/library';

import { VirtualList } from './VirtualList';

/** Pixels: a tile, `--riddim-h`. */
export const RIDDIM_TILE_HEIGHT = 128;
/** Pixels: a row of two tiles and the gap under it — `--riddim-h` + `--s-3`. */
export const RIDDIM_ROW_HEIGHT = RIDDIM_TILE_HEIGHT + 12;

interface TileRow {
  readonly key: string;
  readonly height: number;
  /** The place of the row's first tile in the whole list: every third tile is gold. */
  readonly first: number;
  readonly riddims: readonly RiddimItem[];
}

export function RiddimTiles({
  riddims,
  label,
  onOpen,
}: {
  readonly riddims: readonly RiddimItem[];
  readonly label: string;
  readonly onOpen: (collection: Collection) => void;
}): JSX.Element {
  const rows = useMemo<TileRow[]>(() => {
    const out: TileRow[] = [];
    for (let first = 0; first < riddims.length; first += 2) {
      const pair = riddims.slice(first, first + 2);
      out.push({
        key: `r:${pair.map((riddim) => String(riddim.id)).join('-')}`,
        height: RIDDIM_ROW_HEIGHT,
        first,
        riddims: pair,
      });
    }
    return out;
  }, [riddims]);

  const renderRow = useCallback(
    (row: TileRow): JSX.Element => (
      <li className="riddimRow">
        {row.riddims.map((riddim, at) => (
          <button
            key={riddim.id}
            type="button"
            className="riddimTile"
            data-gold={(row.first + at) % 3 === 1 ? 'true' : undefined}
            aria-label={`${riddim.name} · ${trackCount(riddim.trackCount)}`}
            onClick={() => {
              onOpen({ kind: 'riddim', id: riddim.id, name: riddim.name });
            }}
          >
            <span className="riddimKind">Riddim</span>
            <span className="riddimName">{riddim.name}</span>
            <span className="riddimCount">{trackCount(riddim.trackCount)}</span>
          </button>
        ))}
      </li>
    ),
    [onOpen],
  );

  return <VirtualList rows={rows} renderRow={renderRow} as="ul" ariaLabel={label} />;
}
